/**
 * 配信本文の break-glass 閲覧 `/break-glass`（A128 / A305 / A321）。
 *
 * **本文は原則として運営者にも読めない**（不変条件 9 / CMS_DESIGN D40）。
 * それでも法執行機関の正式な要請や重大な不正利用の調査では読む必要が出る。
 * 経路を作らないと実際に起きるのは「諦める」ではなく「S3 のコンソールから
 * 直接取ってくる」で、そのときは監査が丸ごと消える。だから
 * **経路を 1 本だけ作り、全部記録する**。
 *
 * この画面が守っている統制（サーバー側の実装と 1:1）:
 *
 *   1. 申請          … 理由 10 文字以上・対象の実在確認・時限（最大 72 時間）
 *   2. 承認          … **申請者以外**の運営者だけ（サーバーが 403 で弾く）
 *   3. 消費          … 付与された本人だけ・**1 回だけ**・読んだら契約者へ通知
 *
 * 承認待ちの申請は DB ではなく Redis に置かれていて **24 時間で消える**
 * （`break_glass_grants` は「承認済みの付与」しか表現できない形になっており、
 * 自己承認の禁止を DB の CHECK で守るためにその形を崩さない、という
 * API 側の設計に由来する）。一覧から申請が消えるのは異常ではないので、
 * 画面でも失効時刻を出して誤解させない。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Check, EyeOff, KeyRound, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  PageHeader,
  StatusBadge,
  Textarea,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { BREAK_GLASS_TARGET_LABEL, labelFrom, optionsFromLabels } from '../lib/ops-labels.js';
import { useAuth, usePermission } from '../stores/auth.js';
import { CodeBlock } from '../components/code-block.js';
import { ConfirmTextDialog, FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { PageTabs } from '../components/page-tabs.js';
import { PermissionDenied, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  BreakGlassApproveResult,
  BreakGlassConsumeResult,
  BreakGlassGrantEntry,
  BreakGlassRequestEntry,
  BreakGlassTargetType,
  CountedList,
  StaffUser,
} from '../types/api.js';

const routeApi = getRouteApi(RID.breakGlass);

/** 承認待ちが Redis から消えるまで（サーバーの `REQUEST_TTL_SEC` と同じ）。 */
const REQUEST_TTL_HOURS = 24;

/** 付与の有効期間の上限（サーバーの `MAX_GRANT_HOURS` と同じ）。 */
const MAX_GRANT_HOURS = 72;

export function BreakGlassPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  /*
   * 参照だけの権限は無い。申請・承認・閲覧のすべてが `ops.content.read_body`
   * で守られているので、持っていない運営者には画面ごと出さない
   * （出しても全部 403 になる）。
   */
  const canUse = usePermission(OPS_PERMISSION.contentReadBody);

  if (!canUse) return <PermissionDenied />;

  return (
    <>
      <PageHeader
        title="本文の閲覧（break-glass）"
        description="配信内容の本文を読むための例外経路。申請・別の運営者による承認・1 回きりの閲覧がすべて記録され、閲覧した事実は契約者へ通知されます。"
      />

      <Alert variant="warning" className="mb-4">
        <AlertTitle>通常の運用でここを使うことはありません</AlertTitle>
        <AlertDescription>
          法令に基づく要請、または重大な不正利用の調査に限って使います。理由・対象・閲覧した運営者は
          監査ログに残り、抑止しない限り契約者へ「当社が本文を閲覧しました」という通知が届きます。
          {/*
           * `ops.content.read_body` は fresh-auth 対象（rbac.ts の
           * FRESH_AUTH_PERMISSIONS）。**一覧を開くだけでも**再認証を求められる
           * ことがあるので、先に書いておく（ダイアログが出た時点で不具合を疑わせない）。
           */}
          <span className="mt-1 block">
            この画面の操作は一覧の表示を含めて再認証（2 要素目の入力）を求められることがあります。
          </span>
        </AlertDescription>
      </Alert>

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'requests', label: '承認待ち', icon: ShieldAlert },
          { value: 'grants', label: '付与', icon: KeyRound },
        ]}
      />

      {search.tab === 'grants' ? <GrantsTab /> : <RequestsTab />}
    </>
  );
}

/* ------------------------------------------------------------ 承認待ち */

function RequestsTab(): React.ReactElement {
  const { user } = useAuth();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [withdrawing, setWithdrawing] = React.useState<BreakGlassRequestEntry | null>(null);

  const query = useQuery({
    queryKey: qk.breakGlassRequests(),
    queryFn: () => api.get<CountedList<BreakGlassRequestEntry>>('/break-glass/requests'),
    // 承認を待っている側は「承認されたか」を知りたい。短めに引き直す。
    refetchInterval: 15_000,
  });

  const staffNames = useStaffNames();

  const columns: Array<DataTableColumn<BreakGlassRequestEntry>> = [
    {
      id: 'target',
      header: '対象',
      cell: (r) => (
        <div className="space-y-0.5">
          <span className="block font-medium">{labelFrom(BREAK_GLASS_TARGET_LABEL, r.targetType)}</span>
          <span className="block font-mono text-xs text-muted-foreground">{r.targetId}</span>
        </div>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      cell: (r) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: r.organizationId }}
          search={{ tab: 'overview' }}
          className="font-mono text-xs hover:text-primary"
        >
          {r.organizationId}
        </Link>
      ),
    },
    { id: 'requester', header: '申請者', hideBelow: 'md', cell: (r) => staffNames(r.requesterStaffUserId) },
    {
      id: 'reason',
      header: '理由',
      cell: (r) => <span className="text-xs text-muted-foreground">{r.reason}</span>,
    },
    {
      id: 'grant',
      header: '時限',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => `${r.grantHours} 時間`,
    },
    {
      id: 'notice',
      header: '通知',
      hideBelow: 'lg',
      cell: (r) =>
        r.noticeSuppressed ? (
          <Badge variant="destructive" size="sm">
            <EyeOff aria-hidden />
            抑止
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground">契約者へ通知</span>
        ),
    },
    {
      id: 'requestedAt',
      header: '申請',
      hideBelow: 'md',
      cell: (r) => (
        <div className="space-y-0.5">
          <span className="block text-xs">{formatDateTime(r.requestedAt)}</span>
          <span className="block text-[11px] text-muted-foreground">
            {expiryLabel(r.requestedAt)}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-52',
      cell: (r) => (
        <RequestActions
          request={r}
          isMine={r.requesterStaffUserId === (user?.id ?? '')}
          onWithdraw={() => setWithdrawing(r)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-end gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus aria-hidden />
          閲覧を申請
        </Button>
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        emptyTitle="承認待ちの申請はありません"
        emptyDescription={`申請は ${REQUEST_TTL_HOURS} 時間で自動的に失効します。失効したものは一覧から消えますが、申請した事実は監査ログに残ります。`}
      />

      <RequestDialog open={createOpen} onOpenChange={setCreateOpen} />
      <WithdrawDialog request={withdrawing} onClose={() => setWithdrawing(null)} />
    </>
  );
}

/**
 * 承認 / 取り下げ。
 *
 * **自分の申請には承認ボタンを出さない。** サーバーも 403 で弾くが、
 * 押せてしまうと「承認したつもり」で待たれる。押せない理由を文字で出す。
 */
function RequestActions({
  request,
  isMine,
  onWithdraw,
}: {
  request: BreakGlassRequestEntry;
  isMine: boolean;
  onWithdraw: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [approved, setApproved] = React.useState<BreakGlassApproveResult | null>(null);

  const approve = useMutation({
    mutationFn: () =>
      api.post<BreakGlassApproveResult>(`/break-glass/requests/${request.id}/approve`, {}),
    onSuccess: (res) => {
      setApproved(res);
      void qc.invalidateQueries({ queryKey: ['ops', 'break-glass'] });
    },
  });

  return (
    <div className="flex items-center justify-end gap-1">
      {isMine ? (
        <span className="text-[11px] text-muted-foreground">申請者自身は承認できません</span>
      ) : (
        <Button variant="outline" size="sm" onClick={() => approve.mutate()} loading={approve.isPending}>
          <Check aria-hidden />
          承認
        </Button>
      )}
      {isMine ? (
        <Button variant="ghost" size="icon-sm" aria-label="申請を取り下げる" onClick={onWithdraw}>
          <Trash2 aria-hidden />
        </Button>
      ) : null}

      <Dialog open={approved !== null} onOpenChange={(next) => !next && setApproved(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>閲覧を承認しました</DialogTitle>
            <DialogDescription>
              申請者だけが 1 回だけ本文を読めます。<strong>あなたには読む権限はありません</strong>（読む人と承認する人を必ず分けます）。
            </DialogDescription>
          </DialogHeader>
          {approved ? (
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">付与 ID</dt>
                <dd className="font-mono text-xs">{approved.grantId}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted-foreground">期限</dt>
                <dd>{formatDateTime(approved.expiresAt)}</dd>
              </div>
            </dl>
          ) : null}
          <DialogFooter>
            <Button type="button" onClick={() => setApproved(null)}>
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** 申請の取り下げ（`DELETE /ops/break-glass/requests/:id`）。 */
function WithdrawDialog({
  request,
  onClose,
}: {
  request: BreakGlassRequestEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => api.del(`/break-glass/requests/${request?.id ?? ''}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'break-glass'] });
      onClose();
    },
  });

  return (
    <ConfirmTextDialog
      open={request !== null}
      onOpenChange={(next) => !next && onClose()}
      title="申請を取り下げる"
      description="承認前の申請を消します。申請した事実と理由は監査ログに残ります。"
      confirmText={request?.targetId ?? ''}
      confirmLabel="取り下げる"
      pending={mut.isPending}
      error={mut.error}
      onConfirm={() => mut.mutate()}
    />
  );
}

/** 閲覧の申請（`POST /ops/break-glass/requests`。body は `.strict()`）。 */
function RequestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [targetType, setTargetType] = React.useState<BreakGlassTargetType>('campaign');
  const [targetId, setTargetId] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [grantHours, setGrantHours] = React.useState('2');
  const [noticeSuppressed, setNoticeSuppressed] = React.useState(false);
  const [noticeSuppressedReason, setNoticeSuppressedReason] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setTargetType('campaign');
    setTargetId('');
    setReason('');
    setGrantHours('2');
    setNoticeSuppressed(false);
    setNoticeSuppressedReason('');
  }, [open]);

  const submit = useMutation({
    mutationFn: () =>
      api.post('/break-glass/requests', {
        targetType,
        targetId: targetId.trim(),
        reason: reason.trim(),
        grantHours: Number(grantHours),
        noticeSuppressed,
        // **未知キーは 400**（`.strict()`）。抑止しないときは理由ごと送らない。
        ...(noticeSuppressed ? { noticeSuppressedReason: noticeSuppressedReason.trim() } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'break-glass'] });
      onOpenChange(false);
    },
  });

  // サーバー側の zod と同じ条件で先に止める（理由 10 文字以上・時限 1〜72）。
  const hours = Number(grantHours);
  const invalid =
    targetId.trim().length === 0 ||
    reason.trim().length < 10 ||
    !Number.isInteger(hours) ||
    hours < 1 ||
    hours > MAX_GRANT_HOURS ||
    (noticeSuppressed && noticeSuppressedReason.trim().length < 10);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="本文の閲覧を申請する"
      description="申請だけでは何も読めません。別の運営者が承認して初めて、あなたに 1 回きりの閲覧が付与されます。"
      submitLabel="申請する"
      pending={submit.isPending}
      error={submit.error}
      disabled={invalid}
      onSubmit={() => submit.mutate()}
      wide
    >
      <Field label="対象の種別" required>
        <NativeSelect
          value={targetType}
          onChange={(v) => setTargetType(v as BreakGlassTargetType)}
          options={optionsFromLabels(BREAK_GLASS_TARGET_LABEL).filter((o) => o.value !== '')}
        />
      </Field>

      <Field
        label="対象 ID"
        required
        hint="一斉配信の一覧・組織詳細からコピーします。存在しない ID では申請できません（承認者が中身を確かめられないため）。"
      >
        <Input
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          spellCheck={false}
          autoComplete="off"
          className="font-mono text-xs"
        />
      </Field>

      <Field
        label="理由"
        required
        hint="10 文字以上。承認者の判断材料になり、そのまま契約者への通知にも載ります。"
        error={reason.length > 0 && reason.trim().length < 10 ? '10 文字以上で入力してください。' : undefined}
      >
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="○○地方検察庁からの捜査関係事項照会（令和8年8月20日付・文書番号 …）への回答のため"
        />
      </Field>

      <Field
        label="有効時間"
        required
        hint={`承認された瞬間から数えます（1〜${MAX_GRANT_HOURS} 時間）。長い時限は「常時読める」のと同じなので短くします。`}
      >
        <Input
          type="number"
          min={1}
          max={MAX_GRANT_HOURS}
          value={grantHours}
          onChange={(e) => setGrantHours(e.target.value)}
        />
      </Field>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          checked={noticeSuppressed}
          onChange={(e) => setNoticeSuppressed(e.target.checked)}
          className="mt-0.5 size-4 rounded border-border"
        />
        <span>
          契約者への通知を抑止する
          <span className="block text-xs text-muted-foreground">
            捜査上の理由などで通知できない場合のみ。<strong>抑止した事実と理由は付与の行に必ず残ります</strong>（隠せません）。
          </span>
        </span>
      </label>

      {noticeSuppressed ? (
        <Field label="抑止する理由" required hint="10 文字以上。DB の CHECK でも必須になっています。">
          <Textarea
            value={noticeSuppressedReason}
            onChange={(e) => setNoticeSuppressedReason(e.target.value)}
            rows={2}
            placeholder="捜査機関から通知の留保を要請されているため（要請書 …）"
          />
        </Field>
      ) : null}
    </FormDialog>
  );
}

/* ---------------------------------------------------------------- 付与 */

function GrantsTab(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const { user } = useAuth();
  const [consuming, setConsuming] = React.useState<BreakGlassGrantEntry | null>(null);

  const query = useQuery({
    queryKey: qk.breakGlassGrants({ active: search.active }),
    /*
     * **`active` は「有効なものだけ」のときにだけ送る。**
     * サーバーは `z.coerce.boolean()` で受けており、`active=0` は
     * 文字列 `'0'` として **true** に化ける（空でない文字列は全部 true）。
     * 「全部見る」は値を送らないことで表現する。
     */
    queryFn: () =>
      api.get<CountedList<BreakGlassGrantEntry>>('/break-glass', {
        active: search.active ? 1 : undefined,
      }),
  });

  const staffNames = useStaffNames();

  const columns: Array<DataTableColumn<BreakGlassGrantEntry>> = [
    {
      id: 'state',
      header: '状態',
      width: 'w-28',
      cell: (g) =>
        g.consumedAt ? (
          <StatusBadge status="consumed" size="sm" label="閲覧済み" tone="muted" />
        ) : g.active ? (
          <StatusBadge status="active" size="sm" dot label="有効" tone="warning" />
        ) : (
          <StatusBadge status="expired" size="sm" label="期限切れ" tone="muted" />
        ),
    },
    {
      id: 'target',
      header: '対象',
      cell: (g) => (
        <div className="space-y-0.5">
          <span className="block font-medium">{labelFrom(BREAK_GLASS_TARGET_LABEL, g.targetType)}</span>
          <span className="block font-mono text-xs text-muted-foreground">{g.targetId}</span>
        </div>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      hideBelow: 'md',
      cell: (g) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: g.organizationId }}
          search={{ tab: 'overview' }}
          className="font-mono text-xs hover:text-primary"
        >
          {g.organizationId}
        </Link>
      ),
    },
    {
      id: 'people',
      header: '閲覧者 / 承認者',
      hideBelow: 'md',
      cell: (g) => (
        <div className="space-y-0.5 text-xs">
          <span className="block">{staffNames(g.staffUserId)}</span>
          <span className="block text-muted-foreground">承認 {staffNames(g.approverStaffUserId)}</span>
        </div>
      ),
    },
    {
      id: 'reason',
      header: '理由',
      hideBelow: 'lg',
      cell: (g) => (
        <div className="space-y-0.5">
          <span className="block text-xs text-muted-foreground">{g.reason}</span>
          {g.noticeSuppressed ? (
            <Badge variant="destructive" size="sm">
              <EyeOff aria-hidden />
              通知抑止
            </Badge>
          ) : null}
        </div>
      ),
    },
    {
      id: 'when',
      header: '期限 / 閲覧',
      hideBelow: 'md',
      cell: (g) => (
        <div className="space-y-0.5 text-xs">
          <span className="block">期限 {formatDateTime(g.expiresAt)}</span>
          <span className="block text-muted-foreground">
            {g.consumedAt ? `閲覧 ${formatRelative(g.consumedAt)}` : `付与 ${formatRelative(g.grantedAt)}`}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">操作</span>,
      align: 'right',
      width: 'w-44',
      cell: (g) =>
        /*
         * **付与された本人にしか出さない。** 承認者にも読む権限は無い
         * （サーバーの consume が `staffUserId !== actor.id` を 403 で弾く）。
         */
        g.active && g.staffUserId === (user?.id ?? '') ? (
          <Button variant="outline" size="sm" onClick={() => setConsuming(g)}>
            <BookOpen aria-hidden />
            本文を読む
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-end gap-2">
        <RefetchingDot active={query.isFetching && !query.isLoading} />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={search.active}
            onChange={(e) => void navigate({ search: (prev) => ({ ...prev, active: e.target.checked }) })}
            className="size-4 rounded border-border"
          />
          有効なものだけ
        </label>
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(g) => g.id}
        loading={query.isLoading}
        emptyTitle={search.active ? '有効な付与はありません' : '付与の記録はありません'}
        emptyDescription="承認された申請がここに並びます。1 回読むと以後は「閲覧済み」として残り続けます。"
      />

      <ConsumeDialog grant={consuming} onClose={() => setConsuming(null)} />
    </>
  );
}

/**
 * 付与を使って本文を読む（`POST /ops/break-glass/grants/:id/consume`）。
 *
 * **1 回しか成功しない。** 消費の記録は本文を返す前に同じトランザクションで
 * 書かれる（記録だけが残る側に倒してある）ので、この応答を取り落としたら
 * 本文は二度と読めない。だから確認を挟み、開いた本文はダイアログを閉じるまで
 * 保持して、コピー・保存できる形で出す。
 */
function ConsumeDialog({
  grant,
  onClose,
}: {
  grant: BreakGlassGrantEntry | null;
  onClose: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [result, setResult] = React.useState<BreakGlassConsumeResult | null>(null);

  const consume = useMutation({
    mutationFn: () =>
      api.post<BreakGlassConsumeResult>(`/break-glass/grants/${grant?.id ?? ''}/consume`, {}),
    onSuccess: (res) => {
      setResult(res);
      void qc.invalidateQueries({ queryKey: ['ops', 'break-glass'] });
    },
  });

  const close = (): void => {
    setResult(null);
    onClose();
  };

  // 本文を受け取った後は、確認ダイアログではなく本文ビューアを出す。
  if (result) {
    return (
      <Dialog open onOpenChange={(next) => !next && close()}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>本文（1 回限りの閲覧）</DialogTitle>
            <DialogDescription>
              閲覧は記録済みです。<strong>このダイアログを閉じると二度と開けません。</strong>必要なら今すぐ保存してください。
            </DialogDescription>
          </DialogHeader>

          <dl className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <dt className="text-muted-foreground">対象</dt>
              <dd className="font-mono">{result.targetId}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">オブジェクトキー</dt>
              <dd className="break-all font-mono">{result.objectKey}</dd>
            </div>
          </dl>

          {result.body === null ? (
            <Alert variant="warning">
              <AlertTitle>本文の実体は残っていません</AlertTitle>
              <AlertDescription>
                保持期間を過ぎて刈り取られています（付与の記録だけが残ります）。
              </AlertDescription>
            </Alert>
          ) : (
            <CodeBlock
              value={result.body}
              language={result.contentType}
              filename={`break-glass-${result.targetId}.txt`}
              maxHeight="24rem"
            />
          )}

          <DialogFooter>
            <Button type="button" onClick={close}>
              閉じる
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <ConfirmTextDialog
      open={grant !== null}
      onOpenChange={(next) => !next && close()}
      title="本文を読む"
      description="読んだ瞬間に記録され、通知を抑止していない限り契約者へ通知が届きます。付与は 1 回しか使えません。"
      confirmText={grant?.targetId ?? ''}
      confirmLabel="読む"
      pending={consume.isPending}
      error={consume.error}
      onConfirm={() => consume.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          {grant?.noticeSuppressed
            ? 'この付与は通知を抑止しています。抑止した理由は付与の記録に残ります。'
            : '閲覧すると契約者へ「当社が本文を閲覧しました」という通知が届きます。'}
        </AlertDescription>
      </Alert>
    </ConfirmTextDialog>
  );
}

/* ---------------------------------------------------------------- 補助 */

/**
 * staff ID を表示名に直す。
 *
 * API は break-glass の応答に ID しか載せない（表示名の解決はしていない）。
 * ID だけを並べると「誰が申請して誰が承認したのか」が読めないので、
 * `GET /ops/staff` を 1 回引いて画面側で引き当てる。権限が無ければ ID のまま出す。
 */
function useStaffNames(): (staffUserId: string) => string {
  const canListStaff = usePermission(OPS_PERMISSION.staffRead);
  const staff = useQuery({
    queryKey: qk.staff(),
    queryFn: () => api.get<{ items: StaffUser[] }>('/staff'),
    enabled: canListStaff,
    staleTime: 5 * 60_000,
  });

  return React.useCallback(
    (staffUserId: string) => {
      const hit = (staff.data?.items ?? []).find((s) => s.id === staffUserId);
      return hit ? hit.displayName : staffUserId;
    },
    [staff.data],
  );
}

/** 申請の失効（申請時刻 + 24 時間）を「あと n 時間」の形で出す。 */
function expiryLabel(requestedAt: string): string {
  const expires = new Date(requestedAt).getTime() + REQUEST_TTL_HOURS * 3600 * 1000;
  if (Number.isNaN(expires)) return '';
  return `承認期限 ${formatRelative(new Date(expires).toISOString())}`;
}
